import * as Sentry from '@sentry/react'
import classnames from 'classnames'
import { useEffect, useRef } from 'react'

import { Alert, AlertProps, Button } from '@/components'

interface TeslaAnnouncementAlertProps extends AlertProps {
    variant?: 'a' | 'b' | 'c'
}

export const TeslaAnnouncementAlert = ({
    variant = 'a',
    className,
    ...props
}: TeslaAnnouncementAlertProps) => {
    const isVariantA = variant === 'a'
    const isVariantB = variant === 'b'
    const isVariantC = variant === 'c'
    const feedbackButtonRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        const element = feedbackButtonRef.current

        if (element && !element.classList.contains('sentry-feedback-attached')) {
            const feedback = Sentry.feedbackIntegration({
                colorScheme: 'system',
                isNameRequired: true,
                isEmailRequired: true,
                showBranding: false,
                useSentryUser: {
                    email: 'email',
                    name: 'username'
                },
                isRequiredLabel: '*'
            })
            feedback.attachTo(element, {
                formTitle: 'Submit Feedback for the Tesla Beta',
                messagePlaceholder: 'What questions or feedback do you have about the Tesla Beta? How can we improve' +
                    ' and provide more value?',
                submitButtonLabel: 'Send Feedback',
                successMessageText: 'Thank you for your feedback!'
            })
            element.classList.add('sentry-feedback-attached')
        }
    }, [])

    return <Alert
        type="custom"
        icon={<svg width="68" height="68" viewBox="0 0 68 68" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="68" height="68" rx="10" fill="#202024"/>
            <mask
                id="nl94y7jlza"
                style={{ maskType: 'luminance' }}
                maskUnits="userSpaceOnUse"
                x="14"
                y="14"
                width="41"
                height="41"
            >
                <path d="M54.914 14.168H14.166v40.748h40.748V14.168z" fill="#fff"/>
            </mask>
            <g mask="url(#nl94y7jlza)">
                <path d="M54.914 14.168H14.166v40.748h40.748V14.168z" fill="#202024"/>
                <path
                    d="m34.54 23.267-4.198-5.133s-7.21.15-14.385 3.49c1.838 2.772 5.485 4.136 5.485 4.136.248-2.443 1.962-3.04 7.396-3.04l5.7 32.196 5.701-32.198c5.436 0 7.148.6 7.396 3.04 0 0 3.647-1.365 5.485-4.136-7.175-3.338-14.385-3.49-14.385-3.49l-4.198 5.134.003.001z"
                    fill="#fff"
                />
                <path
                    d="M34.537 14.168c-.101 0-.2.001-.297.004-5.924.057-12.77 1.082-20.074 3.984 0 0 .233.725 1.142 2.366 6.796-2.973 13.435-3.919 19.235-3.875h.05c5.785-.038 12.404.91 19.178 3.875.909-1.642 1.142-2.366 1.142-2.366-7.428-2.952-14.386-3.962-20.376-3.988z"
                    fill="#fff"
                />
            </g>
        </svg>}
        className={classnames('bg-gradient-to-r rounded-xl border border-transparent', className)}
        style={{
            background: 'linear-gradient(to right, var(--color-yellow-200), var(--color-blue-400)) padding-box, ' +
                'linear-gradient(to right, var(--color-blue-500), var(--color-yellow-300)) border-box'
        }}
        {...props}
    >

        <div className="flex gap-2 md:gap-3 flex-col lg:flex-row items-start lg:items-center justify-between grow">
            <div>
                <h3 className="font-semibold text-base">
                    <span className="inline-block text-sm text-gray-500 border border-gray-200 bg-gray-50 rounded-2xl px-2.5 py-0.5 mr-2">
                        NEW
                    </span>
                    Tesla Monitoring Beta is Live!
                    {(isVariantB || isVariantC) && <span className="font-normal italic">(Participation is optional)</span>}
                </h3>
                {isVariantA && <p className="mt-0.5 leading-6 text-base">
                    <span className="italic">Want better monitoring and faster claims for Tesla systems?</span> Take
                    advantage of the beta to get more from Daybreak — the more warranties you connect, the more you
                    gain.
                </p>}
                {isVariantB && <p className="mt-0.5 leading-6 text-base">
                    <span className="font-semibold italic">Manual site-level site sharing is not required while the
                    beta is active. Any Errors can be ignored.</span><br/>
                    However, connecting your Tesla systems now will improve monitoring and streamline future claims.
                </p>}
                {isVariantC && <p className="mt-0.5 leading-6 text-base">
                    <span className="font-semibold italic">Tesla Credentials are not required while the beta is active.
                    Any Errors can be ignored.  </span><br/>
                    However, connecting your Tesla systems now will improve monitoring and streamline future claims.
                </p>}
            </div>

            <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
                <Button
                    design="btn-secondary-gray"
                    size="btn-lg"
                    className="whitespace-nowrap w-full md:w-auto"
                    href="https://www.solarinsure.com/tesla-daybreak-monitoring"
                    target="_blank"
                >
                    Learn More
                </Button>
                <Button
                    design="btn-secondary-gray"
                    size="btn-lg"
                    className="whitespace-nowrap w-full md:w-auto"
                    ref={feedbackButtonRef}
                >
                    Submit Feedback
                </Button>
            </div>
        </div>
    </Alert>
}
